import React, { useState, useEffect } from 'react';
import { bool, func, object, oneOf, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect, useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import swal from 'sweetalert2';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { camelize } from '../../util/string';
import {
  createResourceLocatorString,
  pathByRouteName,
} from '../../util/routes';
import { apiBaseUrl } from '../../util/api';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { isSignupEmailTakenError } from '../../util/errors';

import {
  login,
  authenticationInProgress,
  signup,
  signupWithIdp,
  clearErrors,
} from '../../ducks/auth.duck';
import {
  isScrollingDisabled,
  manageDisableScrolling,
} from '../../ducks/ui.duck';

import {
  Page,
  Heading,
  NamedRedirect,
  NamedLink,
  LinkTabNavHorizontal,
  SocialLoginButton,
  ResponsiveBackgroundImageContainer,
  Modal,
  LayoutSingleColumn,
  IconArrowHead,
} from '../../components';
import LayoutTwoColumn from '../../components/LayoutTwoColumn/LayoutTwoColumn';
import PromotionalContent from './PromotionalContent/PromotionalContent';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import LoginForm from './LoginForm/LoginForm';
import SignupForm from './SignupForm/SignupForm';

// We need to get ToS asset and get it rendered for the modal on this page.
import { TermsOfServiceContent } from '../../containers/TermsOfServicePage/TermsOfServicePage';

// We need to get PrivacyPolicy asset and get it rendered for the modal on this page.
import { PrivacyPolicyContent } from '../../containers/PrivacyPolicyPage/PrivacyPolicyPage';

import {
  TOS_ASSET_NAME,
  PRIVACY_POLICY_ASSET_NAME,
} from './AuthenticationPage.duck';

import { AppleLogo, FacebookLogo, GoogleLogo } from './socialLoginLogos';
import MobileBottomNavbar from '../../components/MobileBottomNavbar/MobileBottomNavbar';
import css from './AuthenticationPage.module.css';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
} from '../../util/urlHelpers';
import { normalizePhoneForCountry } from '../../util/phoneValidation';

// Social login buttons are needed by AuthenticationForms
export const SocialLoginButtonsMaybe = props => {
  const routeConfiguration = useRouteConfiguration();
  const {
    isLogin,
    showFacebookLogin,
    showGoogleLogin,
    from,
    showAppleLogin,
  } = props;
  const showSocialLogins =
    showFacebookLogin || showGoogleLogin || showAppleLogin;

  // Track last used SSO provider
  const [lastUsedProvider, setLastUsedProvider] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lastUsedSSOProvider') || null;
    }
    return null;
  });

  const getDefaultRoutes = () => {
    const baseUrl = apiBaseUrl();

    // Route where the user should be returned after authentication
    // This is used e.g. with EditListingPage and ListingPage
    const fromParam = from ? `from=${from}` : '';

    // Default route where user is returned after successfull authentication
    const defaultReturn = pathByRouteName('LandingPage', routeConfiguration);
    const defaultReturnParam = defaultReturn
      ? `&defaultReturn=${defaultReturn}`
      : '';

    // Route for confirming user data before creating a new user
    const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);
    const defaultConfirmParam = defaultConfirm
      ? `&defaultConfirm=${defaultConfirm}`
      : '';

    return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
  };

  const authWithFacebook = () => {
    // Store provider before redirect
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastUsedSSOProvider', 'facebook');
    }
    const defaultRoutes = getDefaultRoutes();
    const {
      baseUrl,
      fromParam,
      defaultReturnParam,
      defaultConfirmParam,
    } = defaultRoutes;
    window.location.href = `${baseUrl}/api/auth/facebook?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
  };

  const authWithGoogle = () => {
    // Store provider before redirect
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastUsedSSOProvider', 'google');
    }
    const defaultRoutes = getDefaultRoutes();
    const {
      baseUrl,
      fromParam,
      defaultReturnParam,
      defaultConfirmParam,
    } = defaultRoutes;
    window.location.href = `${baseUrl}/api/auth/google?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
  };

  const authWithApple = () => {
    // Store provider before redirect
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastUsedSSOProvider', 'apple');
    }
    const defaultRoutes = getDefaultRoutes();
    const {
      baseUrl,
      fromParam,
      defaultReturnParam,
      defaultConfirmParam,
    } = defaultRoutes;
    window.location.href = `${baseUrl}/api/auth/apple?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
  };

  return showSocialLogins ? (
    <div className={css.idpButtons}>
      {/* <div className={css.socialButtonsOr}>
        <span className={css.socialButtonsOrText}>
          <FormattedMessage id="AuthenticationPage.or" />
        </span>
      </div> */}

      {showFacebookLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton
            onClick={() => authWithFacebook()}
            className={css.socialLoginButton}
          >
            <span className={css.buttonIcon}>{FacebookLogo}</span>
            <span className={css.buttonText}>
              {isLogin ? (
                <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
              ) : (
                <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
              )}
            </span>
            {lastUsedProvider === 'facebook' && (
              <span className={css.lastUsedBadge}>
                <FormattedMessage
                  id="AuthenticationPage.lastUsed"
                  defaultMessage="Last Used"
                />
              </span>
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showGoogleLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithGoogle()}>
            <span className={css.buttonIcon}>{GoogleLogo}</span>
            <span className={css.buttonTextBlack}>
              {isLogin ? (
                <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
              ) : (
                <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
              )}
            </span>
            {lastUsedProvider === 'google' && (
              <span className={css.lastUsedBadge}>
                <FormattedMessage
                  id="AuthenticationPage.lastUsed"
                  defaultMessage="Last Used"
                />
              </span>
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showAppleLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithApple()}>
            <span className={css.buttonIcon}>{AppleLogo}</span>
            <span className={css.buttonTextWhite}>
              {isLogin ? (
                <FormattedMessage id="AuthenticationPage.loginWithApple" />
              ) : (
                <FormattedMessage id="AuthenticationPage.signupWithApple" />
              )}
            </span>
            {lastUsedProvider === 'apple' && (
              <span className={css.lastUsedBadge}>
                <FormattedMessage
                  id="AuthenticationPage.lastUsed"
                  defaultMessage="Last Used"
                />
              </span>
            )}
          </SocialLoginButton>
        </div>
      ) : null}
    </div>
  ) : null;
};

// Tabs for SignupForm and LoginForm
export const AuthenticationForms = props => {
  const {
    isLogin,
    showFacebookLogin,
    showAppleLogin,
    showGoogleLogin,
    from,
    submitLogin,
    loginError,
    idpAuthError,
    signupError,
    authInProgress,
    submitSignup,
    termsAndConditions,
    currentUserLocationInfo,
    config,
    locationName,
    intl,
    onClearErrors,
    showAlmostThereMessage,
    editListingTab,
    onManageDisableScrolling,
  } = props;

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const { signupCountries } = config;

  const fromState = {
    state: {
      ...(from ? { from } : {}),
      ...(editListingTab ? { tab: editListingTab } : {}),
    },
  };
  const tabs = [
    {
      text: (
        <Heading as={!isLogin ? 'h1' : 'h2'} rootClassName={css.tab}>
          <FormattedMessage id="AuthenticationPage.signupLinkText" />
        </Heading>
      ),
      selected: !isLogin,
      linkProps: {
        name: 'SignupPage',
        to: fromState,
      },
    },
    {
      text: (
        <Heading as={isLogin ? 'h1' : 'h2'} rootClassName={css.tab}>
          <FormattedMessage id="AuthenticationPage.loginLinkText" />
        </Heading>
      ),
      selected: isLogin,
      linkProps: {
        name: 'LoginPage',
        to: fromState,
      },
    },
  ];

  const { address, country, city, origin } = currentUserLocationInfo || {};

  // Use state to preserve form values across re-renders and errors
  const [signupFormValues, setSignupFormValues] = useState({
    country: country,
  });

  useEffect(() => {
    if (country) {
      setSignupFormValues({ country: country });
    }
  }, [country]);

  const handleSubmitSignup = values => {
    const { fname, lname, phoneNumber, ...rest } = values;
    const updatedPhonenumber = normalizePhoneForCountry(rest.country, phoneNumber || '');
    const params = {
      firstName: fname.trim(),
      lastName: lname.trim(),
      phoneNumber: updatedPhonenumber,
      ...rest,
    };
    // Preserve form values before submission
    setSignupFormValues(values);

    // Return the promise to let Final Form handle submission state
    return submitSignup(params);
  };

  const loginErrorMessage = intl.formatMessage({
    id: 'AuthenticationPage.loginFailed',
  });

  const idpAuthErrorMessage = intl.formatMessage({
    id: 'AuthenticationPage.idpAuthFailed',
  });

  const signupErrorMessage = isSignupEmailTakenError(signupError)
    ? intl.formatMessage({
      id: 'AuthenticationPage.signupFailedEmailAlreadyTaken',
    })
    : intl.formatMessage({
      id: 'AuthenticationPage.signupFailed',
    });

  const loginOrSignupError =
    isLogin && !!idpAuthError
      ? idpAuthErrorMessage
      : isLogin && !!loginError
        ? loginErrorMessage
        : !!signupError
          ? signupErrorMessage
          : null;

  // Error popup
  useEffect(() => {
    if (loginOrSignupError && loginOrSignupError !== '') {
      const isEmailTakenError = isSignupEmailTakenError(signupError);
      const loginButtonText = intl.formatMessage({
        id: 'AuthenticationPage.logIn',
      });

      swal
        .fire({
          title: '',
          text: loginOrSignupError,
          customClass: {
            htmlContainer: css.error,
            confirmButton: css.errorButton,
          },
          confirmButtonText: isEmailTakenError ? loginButtonText : 'OK',
        })
        .then((result) => {
          if (result.isConfirmed && isEmailTakenError) {
            const loginPath = pathByRouteName('LoginPage', routeConfiguration);
            history.push(loginPath);
          }
          onClearErrors();
        });
    }
  }, [loginOrSignupError, signupError, intl, routeConfiguration, history, onClearErrors]);

  return (
    <div className={css.content}>
      <LinkTabNavHorizontal
        className={css.tabs}
        tabs={tabs}
        isButtonStyle={true}
      />
      <div
        key={isLogin ? 'login' : 'signup'}
        className={
          isLogin ? css.tabContentWrapper : css.tabContentWrapperSignup
        }
      >
        {isLogin ? (
          <LoginForm
            className={css.loginForm}
            onSubmit={submitLogin}
            inProgress={authInProgress}
            showAlmostThereMessage={showAlmostThereMessage}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        ) : (
          <SignupForm
            className={css.signupForm}
            onSubmit={handleSubmitSignup}
            inProgress={authInProgress}
            termsAndConditions={termsAndConditions}
            initialValues={signupFormValues}
            signupCountries={signupCountries}
            locationName={locationName}
            currentUserOrigin={origin}
            country={country}
            showAlmostThereMessage={showAlmostThereMessage}
          />
        )}

        <SocialLoginButtonsMaybe
          isLogin={isLogin}
          showFacebookLogin={false}
          showAppleLogin={showAppleLogin}
          showGoogleLogin={showGoogleLogin}
          from={from}
        />

        <div className={css.alreadyHaveAccount}>
          <FormattedMessage
            id="AuthenticationPage.alreadyHaveAccount"
            values={{
              loginLink: (
                <NamedLink name="LoginPage" className={css.loginLink}>
                  <FormattedMessage id="AuthenticationPage.logIn" />
                </NamedLink>
              ),
            }}
          />
        </div>

        <div className={css.securityMessage}>
          <div className={css.securityIcon}>
            <i className="ri-shield-check-line"></i>
          </div>
          <p>
            <FormattedMessage
              id="AuthenticationPage.securityMessage"
              defaultMessage="The contact details stay secure and are only exchanged between the renter and the owner once the rental is confirmed - never before."
            />
          </p>
        </div>
      </div>
    </div>
  );
};

// Form for confirming information from IdP (e.g. Facebook)
// This is shown before new user is created to Marketplace API
const ConfirmIdProviderInfoForm = props => {
  const {
    authInfo,
    authInProgress,
    confirmError,
    submitSingupWithIdp,
    termsAndConditions,
    currentUserLocationInfo,
    config,
    locationName,
    intl,
    onClearErrors,
    showAlmostThereMessage,
    history,
  } = props;

  const routeConfiguration = useRouteConfiguration();
  const { signupCountries } = config;

  const rawIdpId = authInfo?.idpId ? authInfo?.idpId?.toLowerCase() : null;
  const idpDisplayNames = {
    google: 'Google',
    facebook: 'Facebook',
    apple: 'Apple',
  };
  const normalizeProviderId = providerId => {
    if (!providerId) {
      return null;
    }
    return providerId
      .replace(/^loginwith/i, '')
      .replace(/login$/i, '')
      .trim()
      .toLowerCase();
  };
  const humanizeProviderId = providerId => {
    if (!providerId) {
      return null;
    }
    return providerId
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/(?:^|\s)\S/g, match => match.toUpperCase());
  };
  const normalizedProviderId = normalizeProviderId(rawIdpId);
  const idp =
    idpDisplayNames[normalizedProviderId] ||
    humanizeProviderId(normalizedProviderId || rawIdpId) ||
    null;
  const isGoogleProvider = normalizedProviderId
    ? normalizedProviderId.includes('google')
    : false;
  const isAppleProvider = normalizedProviderId
    ? normalizedProviderId.includes('apple')
    : false;
  const criticalFields = ['email', 'firstName', 'lastName'];
  const missingCriticalIdpData = criticalFields.some(field => {
    const value = authInfo?.[field];
    return !value || (typeof value === 'string' && value.trim() === '');
  });
  const shouldShowPrivacyNote =
    missingCriticalIdpData && (isGoogleProvider || isAppleProvider);
  const privacyNoteMessageId = isGoogleProvider
    ? 'AuthenticationPage.confirmSignupPrivacyNoteGoogle'
    : 'AuthenticationPage.confirmSignupPrivacyNoteApple';

  // ✅ PRESERVE the 'from' parameter from authInfo for redirect after signup
  const redirectAfterSignup = authInfo?.from || null;
  const { address, country, city, origin } = currentUserLocationInfo || {};
  const locationMaybe = !!address
    ? {
      location: {
        search: address,
        selectedPlace: { address, city, origin },
      },
    }
    : {};

  const signupFormInitials = {
    // ...locationMaybe,
    country: !!signupCountries.find(c => c.code === country) ? country : null,
    // Include authInfo data so it persists when form re-initializes (e.g., when country is auto-detected)
    ...(authInfo?.firstName && { firstName: authInfo.firstName }),
    ...(authInfo?.lastName && { lastName: authInfo.lastName }),
    ...(authInfo?.email && { email: authInfo.email }),
  };

  const handleSubmitConfirm = values => {
    const { idpToken, email, firstName, lastName, idpId } = authInfo || {};
    const {
      email: newEmail,
      firstName: newFirstName,
      lastName: newLastName,
      country,
      ...rest
    } = values;

    // Pass email, fistName or lastName to Marketplace API only if user has edited them
    // sand they can't be fetched directly from idp provider (e.g. Facebook)

    const authParams = {
      ...(newEmail !== email && { email: newEmail }),
      ...(newFirstName !== firstName && { firstName: newFirstName }),
      ...(newLastName !== lastName && { lastName: newLastName }),
    };

    const publicData = {
      country,
      permitted: false,
    };
    // If the confirm form has any additional values, pass them forward as user's protected data
    const protectedData = !isEmpty(rest) ? { ...rest } : null;

    submitSingupWithIdp({
      idpToken,
      idpId,
      ...authParams,
      ...(!!protectedData && { protectedData }),
      publicData,
    })
      .then(() => {
        // ✅ REDIRECT to CheckoutPage (or original 'from' page) after successful SSO signup
        if (redirectAfterSignup) {
          setTimeout(() => {
            history.push(redirectAfterSignup);
          }, 100);
        }
      })
      .catch(err => {
        console.error('Signup with IDP failed:', err);
      });
  };

  const confirmErrorMessage = confirmError
    ? isSignupEmailTakenError(confirmError)
      ? intl.formatMessage({
        id: 'AuthenticationPage.signupFailedEmailAlreadyTaken',
      })
      : intl.formatMessage({
        id: 'AuthenticationPage.signupFailed',
      })
    : null;

  useEffect(() => {
    if (confirmErrorMessage && confirmErrorMessage !== '') {
      const isEmailTakenError = isSignupEmailTakenError(confirmError);
      const loginButtonText = intl.formatMessage({
        id: 'AuthenticationPage.logIn',
      });

      swal
        .fire({
          title: '',
          text: confirmErrorMessage,
          customClass: {
            htmlContainer: css.error,
            confirmButton: css.errorButton,
          },
          confirmButtonText: isEmailTakenError ? loginButtonText : 'OK',
        })
        .then((result) => {
          if (result.isConfirmed && isEmailTakenError) {
            const loginPath = pathByRouteName('LoginPage', routeConfiguration);
            history.push(loginPath);
          }
          onClearErrors();
        });
    }
  }, [confirmErrorMessage, confirmError, intl, routeConfiguration, history, onClearErrors]);

  return (
    <div className={css.content}>
      <div className={css.tabContentWrapper}>
        <div className={css.formHeader}>
          <Heading as="h1" rootClassName={css.formTitle}>
            <FormattedMessage
              id="AuthenticationPage.confirmSignupWithIdpTitle"
              values={{ idp }}
            />
          </Heading>

          <p className={css.formSubtitle}>
            <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
          </p>
          {shouldShowPrivacyNote ? (
            <div className={css.formInfoAlert}>
              <span className={css.formInfoAlertIcon} aria-hidden="true">
                <i className="ri-information-line" />
              </span>
              <p className={css.formInfoAlertMessage}>
                <FormattedMessage id={privacyNoteMessageId} />
              </p>
            </div>
          ) : null}
        </div>
        <ConfirmSignupForm
          className={css.form}
          onSubmit={handleSubmitConfirm}
          inProgress={authInProgress}
          termsAndConditions={termsAndConditions}
          authInfo={authInfo}
          idp={idp}
          initialValues={signupFormInitials}
          signupCountries={signupCountries}
          locationName={locationName}
          currentUserOrigin={origin}
          country={country}
          showAlmostThereMessage={showAlmostThereMessage}
        />
      </div>

      <div className={css.securityMessage}>
        <div className={css.securityIcon}>
          <i className="ri-shield-check-line"></i>
        </div>
        <p>
          <FormattedMessage
            id="AuthenticationPage.securityMessage"
            defaultMessage="The contact details stay secure and are only exchanged between the renter and the owner once the rental is confirmed - never before."
          />
        </p>
      </div>
    </div>
  );
};

export const AuthenticationOrConfirmInfoForm = props => {
  const {
    tab,
    authInfo,
    from,
    showFacebookLogin,
    showGoogleLogin,
    showAppleLogin,
    submitLogin,
    submitSignup,
    submitSingupWithIdp,
    authInProgress,
    loginError,
    idpAuthError,
    signupError,
    confirmError,
    termsAndConditions,
    currentUserLocationInfo,
    config,
    intl,
    onClearErrors,
    editListingTab,
    onManageDisableScrolling,
    history,
  } = props;
  const isConfirm = tab === 'confirm';
  const isLogin = tab === 'login';
  const showAlmostThereMessage = !!from;

  // To restrict browser from suggesting auto-complete in location input
  // field, we need to ensure the name is different each time.
  const locationName = uuidv4();
  return isConfirm ? (
    <ConfirmIdProviderInfoForm
      authInfo={authInfo}
      submitSingupWithIdp={submitSingupWithIdp}
      authInProgress={authInProgress}
      confirmError={confirmError}
      termsAndConditions={termsAndConditions}
      currentUserLocationInfo={currentUserLocationInfo}
      config={config}
      locationName={locationName}
      intl={intl}
      onClearErrors={onClearErrors}
      showAlmostThereMessage={showAlmostThereMessage}
      history={history}
    />
  ) : (
    <AuthenticationForms
      isLogin={isLogin}
      showFacebookLogin={showFacebookLogin}
      showGoogleLogin={showGoogleLogin}
      showAppleLogin={showAppleLogin}
      from={from}
      loginError={loginError}
      idpAuthError={idpAuthError}
      signupError={signupError}
      submitLogin={submitLogin}
      authInProgress={authInProgress}
      submitSignup={submitSignup}
      termsAndConditions={termsAndConditions}
      currentUserLocationInfo={currentUserLocationInfo}
      config={config}
      locationName={locationName}
      intl={intl}
      onClearErrors={onClearErrors}
      showAlmostThereMessage={showAlmostThereMessage}
      editListingTab={editListingTab}
      onManageDisableScrolling={onManageDisableScrolling}
    ></AuthenticationForms>
  );
};

const getAuthInfoFromCookies = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const authInfoCookie = Cookies.get('st-authinfo');

  if (!authInfoCookie) {
    return null;
  }

  try {
    return JSON.parse(authInfoCookie.replace('j:', ''));
  } catch (err) {
    console.error('Failed to parse st-authinfo cookie', err);
    return null;
  }
};
const getAuthErrorFromCookies = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const authErrorCookie = Cookies.get('st-autherror');

  if (!authErrorCookie) {
    return null;
  }

  try {
    return JSON.parse(authErrorCookie.replace('j:', ''));
  } catch (err) {
    console.error('Failed to parse st-autherror cookie', err);
    return null;
  }
};

export const AuthenticationPageComponent = props => {
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [authInfo, setAuthInfo] = useState(() => getAuthInfoFromCookies());
  const [authError, setAuthError] = useState(() => getAuthErrorFromCookies());
  const [authCookiesHydrated, setAuthCookiesHydrated] = useState(false);
  const config = useConfiguration();
  const routesConfiguration = useRouteConfiguration();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const authInfoFromCookie = getAuthInfoFromCookies();
    const authErrorFromCookie = getAuthErrorFromCookies();

    setAuthInfo(authInfoFromCookie ?? null);
    setAuthError(authErrorFromCookie ?? null);

    if (authErrorFromCookie) {
      Cookies.remove('st-autherror');
    }

    setAuthCookiesHydrated(true);
  }, []);

  // On mobile, it's better to scroll to top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tosModalOpen, privacyModalOpen]);

  const {
    authInProgress,
    currentUser,
    intl,
    isAuthenticated,
    location,
    loginError,
    scrollingDisabled,
    signupError,
    submitLogin,
    submitSignup,
    confirmError,
    submitSingupWithIdp,
    tab,
    onManageDisableScrolling,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
    currentUserLocationInfo,
    onClearErrors,
    history,
  } = props;

  // History API has potentially state tied to this route
  // We have used that state to store previous URL ("from"),
  // so that use can be redirected back to that page after authentication.
  const locationFrom = location.state?.from || null;
  const authinfoFrom = authInfo?.from || null;
  const from = locationFrom ? locationFrom : authinfoFrom ? authinfoFrom : null;
  const editListingTab = location.state?.tab || null;

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const isLogin = tab === 'login';

  // Already authenticated, redirect away from auth page. Unverified users are no
  // longer blocked here - the global EmailVerificationWidget (anchored in the
  // Topbar) nudges them to verify without gating navigation or booking.
  if (isAuthenticated && from) {
    return <Redirect to={from} />;
  } else if (isAuthenticated && currentUserLoaded) {
    return <NamedRedirect name="LandingPage" />;
  }

  const marketplaceName = config.marketplaceName;
  const schemaTitle = isLogin
    ? intl.formatMessage(
      { id: 'AuthenticationPage.schemaTitleLogin' },
      { marketplaceName }
    )
    : intl.formatMessage(
      { id: 'AuthenticationPage.schemaTitleSignup' },
      { marketplaceName }
    );
  const schemaDescription = isLogin
    ? intl.formatMessage(
      { id: 'AuthenticationPage.schemaDescriptionLogin' },
      { marketplaceName }
    )
    : intl.formatMessage(
      { id: 'AuthenticationPage.schemaDescriptionSignup' },
      { marketplaceName }
    );

  const isConfirm = tab === 'confirm';
  const shouldDelayConfirmRender = isConfirm && !authCookiesHydrated;
  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
      className={css.root}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        mobileBottomNavbar={<MobileBottomNavbar />}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <LayoutTwoColumn
          leftColumn={
            <div className={css.formContainer}>
              {isConfirm ? null : (
                <div className={css.formHeader}>
                  <div
                    key={isLogin ? 'login-header' : 'signup-header'}
                    className={css.formHeaderContent}
                  >
                    <h1 className={css.formTitle}>
                      {isLogin ? (
                        <FormattedMessage id="AuthenticationPage.loginTitle" />
                      ) : (
                        <FormattedMessage id="AuthenticationPage.signupTitle" />
                      )}
                    </h1>
                    <p className={css.formSubtitle}>
                      {isLogin ? (
                        <FormattedMessage id="AuthenticationPage.loginSubtitle" />
                      ) : (
                        <FormattedMessage id="AuthenticationPage.signupSubtitle" />
                      )}
                    </p>
                  </div>
                </div>
              )}

              {!shouldDelayConfirmRender && (
                <AuthenticationOrConfirmInfoForm
                  tab={tab}
                  authInfo={authInfo}
                  from={from}
                  showFacebookLogin={!!process.env.REACT_APP_FACEBOOK_APP_ID}
                  showGoogleLogin={!!process.env.REACT_APP_GOOGLE_CLIENT_ID}
                  showAppleLogin={!!process.env.REACT_APP_APPLE_CLIENT_ID}
                  submitLogin={submitLogin}
                  submitSignup={submitSignup}
                  submitSingupWithIdp={submitSingupWithIdp}
                  authInProgress={authInProgress}
                  loginError={loginError}
                  idpAuthError={authError}
                  signupError={signupError}
                  confirmError={confirmError}
                  currentUserLocationInfo={currentUserLocationInfo}
                  termsAndConditions={
                    <TermsAndConditions
                      onOpenTermsOfService={() => setTosModalOpen(true)}
                      onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
                      intl={intl}
                      className={css.termsAndConditions}
                    />
                  }
                  config={config}
                  intl={intl}
                  onClearErrors={onClearErrors}
                  editListingTab={editListingTab}
                  onManageDisableScrolling={onManageDisableScrolling}
                  history={history}
                />
              )}
            </div>
          }
          rightColumn={<PromotionalContent isLogin={isLogin} intl={intl} />}
        />
      </LayoutSingleColumn>
      <Modal
        id="AuthenticationPage.tos"
        isOpen={tosModalOpen}
        onClose={() => setTosModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.termsWrapper}>
          <TermsOfServiceContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(TOS_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
      <Modal
        id="AuthenticationPage.privacyPolicy"
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.privacyWrapper}>
          <PrivacyPolicyContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(PRIVACY_POLICY_ASSET_NAME)]?.data}
          />
        </div>
      </Modal>
    </Page>
  );
};

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  showSocialLoginsForTests: false,
  privacyAssetsData: null,
  privacyFetchInProgress: false,
  privacyFetchError: null,
  tosAssetsData: null,
  tosFetchInProgress: false,
  tosFetchError: null,
};

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  confirmError: propTypes.error,

  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  tab: oneOf(['login', 'signup', 'confirm']),

  onManageDisableScrolling: func.isRequired,

  // to fetch privacy-policy page asset
  // which is shown in modal
  privacyAssetsData: object,
  privacyFetchInProgress: bool,
  privacyFetchError: propTypes.error,

  // to fetch terms-of-service page asset
  // which is shown in modal
  tosAssetsData: object,
  tosFetchInProgress: bool,
  tosFetchError: propTypes.error,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError } = state.auth;
  const { currentUser, currentUserLocationInfo } = state.user;
  const {
    pageAssetsData: privacyAssetsData,
    inProgress: privacyFetchInProgress,
    error: privacyFetchError,
  } = state.hostedAssets || {};
  const {
    pageAssetsData: tosAssetsData,
    inProgress: tosFetchInProgress,
    error: tosFetchError,
  } = state.hostedAssets || {};

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    privacyAssetsData,
    privacyFetchInProgress,
    privacyFetchError,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
    currentUserLocationInfo,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: params => dispatch(signup(params)),
  submitSingupWithIdp: params => dispatch(signupWithIdp(params)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onClearErrors: () => dispatch(clearErrors()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(AuthenticationPageComponent);

export default AuthenticationPage;
